import React from 'react';

import {
  useDeleteStoryItemMutation,
  useStoryQuery,
  useUpdateStoryItemMutation,
  useCreateStoryItemMutation,
  StoryItemsFragment,
} from '../../../generated/graphql';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import { useManageItem } from '../../../helpers/useManageItem';
import Layout from '../../FormComponents/Layout';
import Form, { InitialValues, initialValues, validationSchema } from './Form';
import { PageItemProps } from '../BasicItem';
import { pagesQuery } from '../../LeftContainer/graphql';

export const StoryItem: React.FC<PageItemProps> = ({ onSaveHandler }) => {
  const { pageId, itemId, isNew, close } = useManageItem();

  const [update, { loading: updating }] = useUpdateStoryItemMutation();
  const [create, { loading: creating }] = useCreateStoryItemMutation();
  const [destroy, { loading: destroying }] = useDeleteStoryItemMutation();

  const { data, loading } = useStoryQuery({
    variables: {
      pageId,
      id: itemId!,
    },
    skip: isNew,
  });

  const item = data?.page?.storyItem;

  const deleteStory = async () => {
    if (itemId) {
      await destroy({
        variables: {
          id: itemId,
          pageId,
        },
        update(cache) {
          deletePageItem({ pageId, itemId, cache });
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    }
  };

  const save = async (values: InitialValues): Promise<void> => {
    const { mediaUrl, aspectRatio, __typename, ...rest } = values.photo;
    const photoData = {
      photo: {
        ...rest,
      },
    };

    if (item?.id) {
      await update({
        variables: {
          attributes: {
            id: item?.id,
            pageId: pageId,
            title: values.title || '',
            description: values.description || null,
            ...photoData,
          },
        },
        update: (_cache, { data }) => close(data?.updateStoryItem),
      });
    } else {
      await create({
        variables: {
          attributes: {
            pageId,
            title: values.title || '',
            description: values.description || null,
            ...photoData,
          },
        },
        update: (cache, { data }) => {
          addItemToPage<StoryItemsFragment | undefined>(cache, pageId, data?.createStoryItem, close);
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    }
    if (onSaveHandler) {
      onSaveHandler();
    }
  };

  return (
    <Layout
      headerText={`${isNew ? 'Add a' : 'Update'} Story`}
      validationSchema={validationSchema}
      initialValues={initialValues(item)}
      onSubmit={save}
      Component={Form}
      loading={loading}
      updating={updating || creating || destroying}
      onDelete={deleteStory}
      close={close}
      item={item}
      pageId={pageId}
      isNew={isNew}
    />
  );
};
